import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import React, { useRef, useState, useEffect } from 'react';

const SingleImageUpload = ({ 
  currentImage = null, 
  onImageChange, 
  uploadEndpoint,
  title = "Image",
  maxSize = 5, // En MB
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImage);
  const fileInputRef = useRef(null);

  // Mettre à jour previewUrl quand currentImage change
  useEffect(() => {
    setPreviewUrl(currentImage);
  }, [currentImage]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = async (file) => {
    // Vérifier la taille
    if (file.size > maxSize * 1024 * 1024) {
      alert(`L'image ne doit pas dépasser ${maxSize}MB`);
      return;
    }

    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      alert('Seules les images sont acceptées');
      return;
    }

    // Créer un aperçu local
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    // Upload vers le serveur
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', file);

      const baseUrl = 'https://api.inklandtattoo.fr';
      const response = await fetch(`${baseUrl}${uploadEndpoint}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }

      const data = await response.json();
      
      // Mettre à jour avec le chemin serveur
      onImageChange(data.path);
      setPreviewUrl(`${baseUrl}${data.path}`);
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload de l\'image');
      setPreviewUrl(currentImage);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setPreviewUrl(null);
    onImageChange(null);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const getImageUrl = () => {
    if (previewUrl) {
      // Si c'est déjà une URL complète
      if (previewUrl.startsWith('http://') || previewUrl.startsWith('https://') || previewUrl.startsWith('blob:')) {
        return previewUrl;
      }
      // Sinon, ajouter le base URL
      const baseUrl = 'https://api.inklandtattoo.fr';
      return `${baseUrl}${previewUrl}`;
    }
    return null;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Image existante ou aperçu */}
      {previewUrl ? (
        <Paper
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Box
            component="img"
            src={getImageUrl()}
            alt="Preview"
            sx={{
              width: '100%',
              maxHeight: 300,
              objectFit: 'contain',
              display: 'block',
            }}
            onError={(e) => {
              console.error('Erreur chargement image');
            }}
          />
          {uploading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.5)',
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: '50%',
            }}
          >
            <IconButton
              size="small"
              onClick={removeImage}
              sx={{ color: 'white' }}
              disabled={uploading}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      ) : (
        /* Zone de drop */
        <Paper
          sx={{
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'grey.600',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            backgroundColor: dragActive ? 'rgba(144, 202, 249, 0.1)' : 'background.paper',
            cursor: 'pointer',
            mb: 2,
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Glissez-déposez une image ici
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            ou cliquez pour sélectionner un fichier
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
            onClick={(e) => {
              e.stopPropagation();
              openFileDialog();
            }}
            disabled={uploading}
          >
            {uploading ? 'Upload en cours...' : 'Sélectionner une image'}
          </Button>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
            Formats acceptés: JPG, PNG, GIF, WebP • Maximum {maxSize}MB
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SingleImageUpload;

